// MIT License - Copyright (c) 2026 Doug McNall

/**
 * Crew Conversation Manager
 * 
 * Manages crew conversations, relationships, and backstory reveals
 * Integrates with CrewManager and EventBus
 */

import crewManager from './CrewManager.js';
import { generateConversation, checkBackstoryReveal, CONVERSATION_TRIGGERS } from '../../data/crewConversations.js';
import EventBus, { EVENT_TYPES } from '../system/EventBus.js';
import STORAGE_KEYS from '../system/STORAGE_KEYS';

class CrewConversationManager {
  constructor() {
    this.relationships = {};
    this.conversationHistory = [];
    this.lastConversationTime = 0;
    this.conversationCooldown = 30000; // 30 seconds between conversations
    
    this.loadFromStorage();
    this.initializeRelationships();
  }

  /**
   * Initialize crew relationships
   */
  initializeRelationships() {
    const crew = crewManager.getAllCrew();
    crew.forEach(crew1 => {
      this.relationships[crew1.id] = this.relationships[crew1.id] || {};
      crew.forEach(crew2 => {
        if (crew1.id !== crew2.id && !this.relationships[crew1.id][crew2.id]) {
          this.relationships[crew1.id][crew2.id] = 50; // Neutral starting relationship
        }
      });
    });
    this.saveToStorage();
  }

  /**
   * Get relationship between two crew members
   */
  getRelationship(crew1Id, crew2Id) {
    if (!this.relationships[crew1Id] || !this.relationships[crew1Id][crew2Id]) {
      return 50; // Default neutral
    }
    return this.relationships[crew1Id][crew2Id];
  }

  /**
   * Adjust relationship between crew members
   */
  adjustRelationship(crew1Id, crew2Id, amount) {
    if (!this.relationships[crew1Id]) this.relationships[crew1Id] = {};
    if (!this.relationships[crew2Id]) this.relationships[crew2Id] = {};
    
    // Adjust both directions
    this.relationships[crew1Id][crew2Id] = Math.max(0, Math.min(100, 
      (this.relationships[crew1Id][crew2Id] || 50) + amount));
    this.relationships[crew2Id][crew1Id] = Math.max(0, Math.min(100, 
      (this.relationships[crew2Id][crew1Id] || 50) + amount));
    
    this.saveToStorage();
  }

  /**
   * Generate random conversation
   */
  generateRandomConversation() {
    const crew = crewManager.getAllCrew();
    if (crew.length < 2) return null;

    // Check cooldown
    const now = Date.now();
    if (now - this.lastConversationTime < this.conversationCooldown) {
      return null;
    }

    // Pick two random crew members
    const crew1 = crew[Math.floor(Math.random() * crew.length)];
    let crew2;
    do {
      crew2 = crew[Math.floor(Math.random() * crew.length)];
    } while (crew2.id === crew1.id);

    // Determine trigger based on current state
    let trigger = CONVERSATION_TRIGGERS.RANDOM;
    const relationship = this.getRelationship(crew1.id, crew2.id);
    const morale = crewManager.getMorale();
    
    if (relationship > 70) {
      trigger = CONVERSATION_TRIGGERS.RELATIONSHIP_HIGH;
    } else if (relationship < 30) {
      trigger = CONVERSATION_TRIGGERS.RELATIONSHIP_LOW;
    } else if (morale < 40) {
      trigger = CONVERSATION_TRIGGERS.LOW_MORALE;
    } else if (morale > 80) {
      trigger = CONVERSATION_TRIGGERS.HIGH_MORALE;
    }

    const conversation = generateConversation(trigger, crew1, crew2);
    if (conversation) {
      // Apply relationship effect
      this.adjustRelationship(crew1.id, crew2.id, conversation.relationshipEffect);
      
      // Add to history
      this.conversationHistory.push(conversation);
      this.lastConversationTime = now;
      
      // Log to game events
      EventBus.emit(EVENT_TYPES.GAME_ALERT, {
        type: 'crew_conversation',
        message: `${conversation.dialogue.line1}\n${conversation.dialogue.line2}`,
        context: conversation.dialogue.context,
        timestamp: conversation.timestamp
      });

      // Check for backstory reveals
      if (relationship > 60) {
        const backstory1 = checkBackstoryReveal(crew1, relationship);
        const backstory2 = checkBackstoryReveal(crew2, relationship);
        
        if (backstory1 && Math.random() < 0.3) {
          EventBus.emit(EVENT_TYPES.GAME_ALERT, {
            type: 'crew_backstory',
            message: backstory1.text,
            context: backstory1.context,
            timestamp: backstory1.timestamp
          });
        }
        
        if (backstory2 && Math.random() < 0.3) {
          EventBus.emit(EVENT_TYPES.GAME_ALERT, {
            type: 'crew_backstory', 
            message: backstory2.text,
            context: backstory2.context,
            timestamp: backstory2.timestamp
          });
        }
      }
    }

    return conversation;
  }

  /**
   * Trigger conversation based on event
   */
  triggerEventConversation(eventType, gameState = {}) {
    const crew = crewManager.getAllCrew();
    if (crew.length < 2) return null;

    let trigger;
    switch (eventType) {
      case 'combat_victory':
        trigger = CONVERSATION_TRIGGERS.AFTER_COMBAT;
        break;
      case 'trade_complete':
        trigger = CONVERSATION_TRIGGERS.AFTER_TRADE;
        break;
      case 'crew_hired':
        trigger = CONVERSATION_TRIGGERS.NEW_CREW;
        break;
      case 'crew_injured':
        trigger = CONVERSATION_TRIGGERS.INJURY;
        break;
      default:
        return this.generateRandomConversation();
    }

    // Pick relevant crew members
    const crew1 = crew[Math.floor(Math.random() * crew.length)];
    let crew2;
    do {
      crew2 = crew[Math.floor(Math.random() * crew.length)];
    } while (crew2.id === crew1.id);

    const conversation = generateConversation(trigger, crew1, crew2, gameState);
    if (conversation) {
      this.adjustRelationship(crew1.id, crew2.id, conversation.relationshipEffect);
      this.conversationHistory.push(conversation);
      
      EventBus.emit(EVENT_TYPES.GAME_ALERT, {
        type: 'crew_conversation',
        message: `${conversation.dialogue.line1}\n${conversation.dialogue.line2}`,
        context: conversation.dialogue.context,
        timestamp: conversation.timestamp
      });
    }

    return conversation;
  }

  /**
   * Get crew relationships summary
   */
  getRelationshipsSummary() {
    const crew = crewManager.getAllCrew();
    const summary = {};
    
    crew.forEach(crewMember => {
      summary[crewMember.id] = {
        name: crewMember.name,
        relationships: {}
      };
      crew.forEach(other => {
        if (crewMember.id !== other.id) {
          const relationship = this.getRelationship(crewMember.id, other.id);
          summary[crewMember.id].relationships[other.id] = {
            name: other.name,
            value: relationship,
            status: relationship > 70 ? 'Friends' : relationship < 30 ? 'Tension' : 'Neutral'
          };
        }
      });
    });
    return summary;
  }

  /**
   * Get conversation history
   */
  getConversationHistory(limit = 10) {
    return this.conversationHistory
      .slice(-limit)
      .reverse();
  }

  /**
   * Update relationships when crew changes
   */
  onCrewChange() {
    this.initializeRelationships();
  }

  /**
   * Periodic conversation check (called by game loop)
   */
  update() {
    // Random chance for conversation every update
    if (Math.random() < 0.001) { // 0.1% chance per update
      this.generateRandomConversation();
    }
  }

  /**
   * Save to localStorage
   */
  saveToStorage() {
    try {
      const data = {
        relationships: this.relationships,
        conversationHistory: this.conversationHistory.slice(-50), // Keep last 50
        lastConversationTime: this.lastConversationTime
      };
      localStorage.setItem(STORAGE_KEYS.CREW_CONVERSATIONS, JSON.stringify(data));
    } catch (error) {
      console.error('[CrewConversationManager] Failed to save to localStorage:', error);
    }
  }

  /**
   * Load from localStorage
   */
  loadFromStorage() {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.CREW_CONVERSATIONS);
      if (data) {
        const parsed = JSON.parse(data);
        this.relationships = parsed.relationships || {};
        this.conversationHistory = parsed.conversationHistory || [];
        this.lastConversationTime = parsed.lastConversationTime || 0;
      }
    } catch (error) {
      console.error('[CrewConversationManager] Failed to load from localStorage:', error);
    }
  }

  /**
   * Reset all data
   */
  reset() {
    this.relationships = {};
    this.conversationHistory = [];
    this.lastConversationTime = 0;
    this.saveToStorage();
  }
}

// Singleton instance
const crewConversationManager = new CrewConversationManager();
export default crewConversationManager;